import { cn } from '../../lib/utils';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/tread-ai/id6755405651';

// App Store campaign links: App Analytics attributes downloads to `ct` only when
// the provider token `pt` is set (App Store Connect → App Analytics → Campaigns).
export const APP_STORE_PT = '';
export const appStoreLink = (campaign?: string) =>
  campaign ? `${APP_STORE_URL}?${APP_STORE_PT ? `pt=${APP_STORE_PT}&` : ''}ct=${encodeURIComponent(campaign)}&mt=8` : APP_STORE_URL;

declare global {
  interface Window { gtag?: (...args: unknown[]) => void }
}

/** GA4 event so we can measure website → App Store clicks by placement. */
const trackClick = (placement: string) => {
  try {
    window.gtag?.('event', 'app_store_click', { placement, page: window.location.pathname, campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined });
  } catch {
    /* analytics must never block the link */
  }
};

/** Black "Download on the App Store" button in Apple's badge style. */
export const AppStoreButton = ({
  className,
  compact = false,
  placement = 'unknown',
  campaign,
}: {
  className?: string;
  compact?: boolean;
  placement?: string;
  /** App Store campaign token, e.g. "roadtrip2026-reddit". */
  campaign?: string;
}) => (
  <a
    href={appStoreLink(campaign)}
    target="_blank"
    rel="noopener noreferrer"
    onClick={() => trackClick(placement)}
    className={cn(
      'inline-flex items-center gap-2.5 rounded-xl bg-black text-white hover:bg-neutral-800 transition-colors',
      compact ? 'h-10 px-3.5' : 'h-14 px-5',
      className
    )}
  >
    <svg className={compact ? 'w-4 h-4' : 'w-6 h-6'} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
    </svg>
    {compact ? (
      <span className="text-sm font-medium">App Store</span>
    ) : (
      <span className="flex flex-col leading-none text-left">
        <span className="text-[11px] opacity-80">Download on the</span>
        <span className="text-[19px] font-semibold tracking-tight mt-0.5">App Store</span>
      </span>
    )}
  </a>
);

/** The tire mark used as the logo (same drawing as the app's tire graphic, simplified). */
export const TireMark = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 40 40" className={className} aria-hidden="true">
    <circle cx="20" cy="20" r="19" fill="#202126" />
    {Array.from({ length: 16 }).map((_, i) => (
      <rect key={i} x="18.6" y="1.8" width="2.8" height="3.6" rx="0.8" fill="#3A3B41" transform={`rotate(${i * 22.5} 20 20)`} />
    ))}
    <circle cx="20" cy="20" r="11" fill="#E9EAEE" />
    {Array.from({ length: 5 }).map((_, i) => (
      <path key={i} d="M20 20 L18.6 11.5 Q20 10.6 21.4 11.5 Z" fill="#6E7078" transform={`rotate(${i * 72} 20 20)`} />
    ))}
    <circle cx="20" cy="20" r="2.6" fill="#2B63E6" />
  </svg>
);
